import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isScrapedContent, LoadingIndicator, useChannelStateContext, useMessageContext } from 'stream-chat-react';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import map from 'lodash/map';

import { MessageText } from './message-text';
import { AttachmentList } from '../stream-attachments/attachment-list';
import { convertHEIF, isHEICOrHEIF, useStreamChatContext } from '../../utils/utils';
import { Messages } from '../../../../../api/api-messages';
import PaymentReminderHeader from './payment-reminder-header';
import { buildImagePath } from '../../../../../config/paths';
import { ChatMessage, MessageBubble } from '@ps-design-system/components/ChatMessage';
import FlagMessageContainer from './message-actions/flag-message-flow/flag-message-container';

export const StreamMessage = ({ message: systemMessage }) => {
  const { message = systemMessage } = useMessageContext();

  const { channel } = useChannelStateContext();
  const { ssoId, accessToken } = useStreamChatContext();

  const [wereAttachmentsRefreshed, setWereAttachmentsRefreshed] = useState(false);
  const [refreshedAttachments, setRefreshedAttachments] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const {
    user = {},
    text,
    id,
    created_at: createdAt,
    attachments: originalAttachments
  } = message;
  const hasAttachments = originalAttachments && 0 < originalAttachments.length;

  const isMyMessage = useMemo(() => message?.user?.id === ssoId, [message]);

  const refreshMessageAttachments = async () => {
    try {
      if (!hasAttachments) {
        setWereAttachmentsRefreshed(true);
        return originalAttachments;
      }
      const attachmentsToRefresh = filter(originalAttachments, (attachment) => !isScrapedContent(attachment));
      let newAttachments = [];

      const response = await new Promise((resolve) => {
        Messages.refreshAttachments({
          channel_id: channel.id,
          assets: attachmentsToRefresh
        }, accessToken, resolve);
      });

      if (200 === response.statusCode) {
        const updatedAssets = response['updated-assets'] || [];
        const heicImages = [];
        const otherAttachments = [];

        forEach(updatedAssets, (asset) => {
          if (isHEICOrHEIF({ name: asset.fallback, ...asset })) {
            heicImages.push(asset);
          } else {
            otherAttachments.push(asset);
          }
        });

        const convertedHEICs = await Promise.all(map(heicImages, async (heic) => {
          const response = await fetch(heic.image_url);
          const blob = await response.blob();
          const file = new File([blob], heic.fallback, { type: heic.type });
          const convertedFile = await convertHEIF(file);
          const url = URL.createObjectURL(convertedFile);
          return {
            ...heic,
            image_url: url,
            fallback: file.name
          };
        }));

        newAttachments = [...newAttachments, ...filter(convertedHEICs), ...otherAttachments];
      }

      setRefreshedAttachments(newAttachments);
      setWereAttachmentsRefreshed(true);
    } catch (e) {
      setWereAttachmentsRefreshed(false);
      console.log('Error when refreshing an attachment');
    }
  };

  useEffect(() => {
    if (!wereAttachmentsRefreshed) {
      refreshMessageAttachments();
    }
  }, [wereAttachmentsRefreshed, message]);

  useEffect(() => {
    if (wereAttachmentsRefreshed) {
      const scrapedAttachments = filter(originalAttachments, isScrapedContent);
      setAttachments([...scrapedAttachments, ...refreshedAttachments]);
    }
  }, [wereAttachmentsRefreshed, message]);

  if ('header' === message?.type) {
    return PaymentReminderHeader();
  }

  if (!user || !createdAt) {
    return null;
  }

  return (
    <div className="StreamMessage">
      <FlagMessageContainer message={message}>
        {({ showFlaggingModal }) => (
          <ChatMessage
            onFlagMessageClicked={showFlaggingModal}
            timestamp={createdAt}
            isSent={isMyMessage}
            avatarUrl={'system' === user.id
              ? buildImagePath('no_avatar_placeholder.svg?v=rb')
              : user.image}
            avatarAlt={user.name}
          >
            {text && <MessageBubble isSent={isMyMessage}>
              <MessageText text={text} id={id}/>
            </MessageBubble>
            }
            {hasAttachments
              && <div style={{
                direction: isMyMessage ? 'rtl' : 'ltr',
                minHeight: 150,
                alignContent: 'center',
                position: 'relative'
              }}>
                {!wereAttachmentsRefreshed && <LoadingIndicator size={64} />}
                {wereAttachmentsRefreshed && <AttachmentList attachments={attachments}/>}
              </div>
            }
          </ChatMessage>
        )}
      </FlagMessageContainer>
    </div>
  );
};

StreamMessage.propTypes = {
  message: PropTypes.object
};
